/* eslint-disable no-undef */
import React, { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Router } from 'react-router-dom';
import { dataDogInit } from 'helpers/dataDog';
import qs from 'query-string';
import { CDN_TYPE_SPORT, getCDNUrl, LocalConfig } from 'utils';

import {
  AccountManager,
  AnalyticsManager,
  ApiManager,
  Atlas,
  BetManager,
  Bus,
  initDebug,
  isNotEmptyObject,
  Logger,
  PreferencesProvider,
  SettingsManager,
  SiteConfigManager,
  SportManager,
  SubscriptionManager,
  TranslationProvider,
} from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import { Messages, TransportManager } from '@atlas/transport';

import Notifications from 'common/Notifications';
import { NotificationsUIProvider } from 'common/Popup/NotificationsUIContext';
// TODO Transform to lazy Loading components
import ToastContainer from 'common/Toast/ToastContainer';
import ContactSupportTeamContainer from 'components/ContactSupportTeamContainer/ContactSupportTeamContainer';
import history from 'components/MainRouter/history';
import GlobalMaintenanceContainer from 'components/MaintenanceContainer/GlobalMaintenanceContainer';
import { SearchInputProvider } from 'components/Search/context/InputProvider';
// TODO: Rework extension integration
import { SelectionProvider } from 'components/SportsbookSelections/SelectionsContext';
import DebugInfo from 'src/components/DebugInfo/DebugInfo';
import { LayoutLoader } from 'src/components/Layout/LayoutLoader';
import subscribeUserOffline from 'src/subscriptions/subscribeUserOffline';
import SelfRegulationProvider from 'src/components/SelfRegulationProvider';

import 'common/Form/formConfig/formConfig';
import '../subscriptions/iframe.bindings';
import '../subscriptions/dataLayer.bindings';
import '../iframe/bus.repeater';

import packageData from '../../package.json';
import ArchivedSportsbook from '../components/Pages/Results';
import resolveFingerprint from '../fingerprint';
import IFrameLayout from '../iframe/IFrameLayout';
import IFrameRouter from '../iframe/IFrameRouter';
import Live from '../iframe/LivePage';
// TODO Transform to lazy Loading pages on MainRouter
import Sportsbook from '../iframe/PrematchPage';
import busEventsMapping from '../subscriptions/bus.bindings';

dataDogInit();
initDebug();

const ShareCss = lazy(() => import('modules/share-css'));

const SuspenseCssComponent = () => (
  <Suspense fallback={<div />}>
    <ShareCss />
  </Suspense>
);

// Initialy resolving location query params
const providedQueryParams = qs.parse(window.location.search);
// If token provided
if (providedQueryParams.token) {
  Logger.debug('token detected');
  ApiManager.setAuthorizationToken(providedQueryParams.token);
  AccountManager.setToken(providedQueryParams.token);
}

if (providedQueryParams.theme) {
  SettingsManager.updateSettings({ theme: providedQueryParams.theme });
}

if (providedQueryParams.footerUrl) {
  SettingsManager.updateSettings({ footerUrl: providedQueryParams.footerUrl });
}

if (providedQueryParams.footerHeight) {
  SettingsManager.updateSettings({ footerHeight: providedQueryParams.footerHeight });
}

SiteConfigManager.setLayoutConfig({
  [COMPONENT_TYPES.SPORT_LIVE]: {
    component: Live,
    defaultPath: '/live',
  },
  [COMPONENT_TYPES.SPORT_PREMATCH]: {
    component: Sportsbook,
    defaultPath: '/sport',
  },
  [COMPONENT_TYPES.RESULTS]: {
    component: ArchivedSportsbook,
    defaultPath: '/results',
  },
});

// Only for development
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const localCmsSettings = require('./cms.settings.js');
  SiteConfigManager.setConfig(localCmsSettings.default);

  // Loading svg sprites from dyeing
  fetch('/dyeing/build/sprite/sprite.svg')
    .then(res => res.text())
    .then(svg => {
      document.body.insertAdjacentHTML('beforeend', svg);
    });
}

// Only for production
if (process.env.NODE_ENV === 'production') {
  // Applying default client application settings
  if (isNotEmptyObject(window.CMS_CONFIG)) {
    const { navigation = {}, page = {} } = window.CMS_CONFIG;

    // // FIXME
    SiteConfigManager.setConfig({ ...page, navigation });
  }
}

// Adding default lang settings if provided
if (providedQueryParams.lang) {
  SettingsManager.updateSettings({
    lang: providedQueryParams.lang,
  });
}

// Adding currency code if provided
if (providedQueryParams.currency) {
  AccountManager.manuallySetCurrency = providedQueryParams.currency;
}

const defaultAcceptOddChanges = SiteConfigManager.getConfig(
  'config.config.betslip.defaultAcceptOddsChangesType',
);
if (defaultAcceptOddChanges) {
  SettingsManager.settings.acceptOddsChanges = defaultAcceptOddChanges;
}

const defaultOddsZeroes = SiteConfigManager.getConfig('config.config.odds_zeroes');
if (defaultOddsZeroes) {
  SettingsManager.settings.oddsZeroes = defaultOddsZeroes;
}

const defaultOddsFractionDigits = SiteConfigManager.getConfig('config.config.odds_fraction_digits');
if (defaultOddsFractionDigits) {
  SettingsManager.settings.oddsFractionDigits = defaultOddsFractionDigits;
}

busEventsMapping(Bus, history);
subscribeUserOffline();

// Disables the ability to long touch and open links in a new window
let timeout = null;

document.addEventListener(
  'touchstart',
  e => {
    const linkParent = e.target.closest('a[href]');

    timeout = setTimeout(() => {
      if (linkParent) {
        if (linkParent.target === '_blank') {
          window.open(linkParent.href);
        } else {
          window.location.href = linkParent.href;
        }
      }
    }, 500);
  },
  false,
);

document.addEventListener(
  'touchend',
  () => {
    if (timeout) clearTimeout(timeout);
  },
  false,
);

document.addEventListener(
  'touchmove',
  () => {
    if (timeout) clearTimeout(timeout);
  },
  false,
);

const mobileGridViewType = SiteConfigManager.getConfig(
  'config.config.sportsbook.mobileGridViewType',
);
if (mobileGridViewType === 'simplified') {
  SettingsManager.settings.showAllSports = true;
  SettingsManager.settings.resetFilter = {};
}

// Resolving stored settings
SettingsManager.resolveSettings();

const { host, protocol } = window.location;
const webSocketProtocol = protocol.startsWith('https') ? 'wss:' : 'ws:';

ApiManager.setHostname('core', `${protocol}//${host}/api/`);
ApiManager.setHostname('cms', `${protocol}//${host}/api/cms-go/v1/site/`);
ApiManager.setHostname('cms/v2', `${protocol}//${host}/api/cms-go/v2/site/`);
ApiManager.setHostname('ws', `${webSocketProtocol}//${host}/ws/notifier2`);
ApiManager.setHostname('media', 'http://app4.front-server.atlas-iac.com');
ApiManager.setHostname('cdn', getCDNUrl(CDN_TYPE_SPORT));
if (process.env.NODE_ENV === 'development') {
  window.$sport = SportManager;
  window.BetManager = BetManager;
  window.SiteConfigManager = SiteConfigManager;
  window.Bus = Bus;
}

// Set Auth Token for API service
ApiManager.setAuthorizationToken(AccountManager.getToken());

/*
  Overriding authentication subscription (need different notification topic)
*/
SubscriptionManager.authorizationSubscription = Messages.authorizedPartnerClientUpdates();

// Initializing api default headers
const defaultHeaders = {
  device: 'mobile',
  version: packageData.version,
};

const projectId = LocalConfig.get('projectId');

// Providing project id if it exist in config
if (projectId) {
  defaultHeaders['x-project-id'] = projectId;
}

// Providing header for local testing
if (process.env.NODE_ENV === 'development') {
  defaultHeaders['x-project-team'] = 'frontend-team';
}

ApiManager.setDefaultHeaders(defaultHeaders);

const wsUrl = new URL(ApiManager.getHostname('ws'));
wsUrl.searchParams.set('projectId', projectId);
if (process.env.NODE_ENV === 'development') {
  // Added for security reason, special WAF rule created on Cloudflare
  wsUrl.searchParams.set('project-team-frontend-team', 'x');
}
const transport = TransportManager.getInstance();
transport.initialize(wsUrl);
transport.setOptions({ projectId });

// Initializing analytics
AnalyticsManager.init({
  fingerprint: () =>
    resolveFingerprint().then(components => window.btoa(JSON.stringify(components))),
});

// NOTE: DO NOT DELETE. Those funcs will be needed when we connect CMS via amazon
// const getTranslationsJson = async (lang) => {
//   const res = await fetch(`${CDN_URL}/f-cdn/${LocalConfig.get('projectId')}/i18n/${lang}.json?timestamp=${Date.now()}`);
//   if (res.ok) {
//     return res.json();
//   }
//   throw Error('Failed to fetch');
// };
// const getTranslations = async ({ lang }) => {
//   let result;
//   try {
//     result = await getTranslationsJson(lang);
//   } catch (e) {
//     result = await getTranslationsJson('ENG');
//   }
//   return result;
// };

const domNode = document.getElementById('root');
const root = createRoot(domNode);

root.render(
  <>
    <SuspenseCssComponent />
    <Atlas>
      {/* TODO: Add getTranslations={getTranslations} if amazon translations be needed */}
      <TranslationProvider>
        <GlobalMaintenanceContainer>
          <ContactSupportTeamContainer>
            <Router history={history}>
              <NotificationsUIProvider isIframe>
                <PreferencesProvider>
                  <SearchInputProvider isIframe>
                    <SelfRegulationProvider>
                      <LayoutLoader>
                        <IFrameLayout>
                          <SelectionProvider>
                            <Suspense fallback={<div />}>
                              <IFrameRouter />
                              <DebugInfo />
                            </Suspense>
                          </SelectionProvider>
                        </IFrameLayout>
                      </LayoutLoader>
                    </SelfRegulationProvider>
                  </SearchInputProvider>
                </PreferencesProvider>

                <Suspense fallback={<div />}>
                  <ToastContainer />
                </Suspense>
                <Suspense fallback={<div />}>
                  <Notifications />
                </Suspense>
              </NotificationsUIProvider>
            </Router>
          </ContactSupportTeamContainer>
        </GlobalMaintenanceContainer>
      </TranslationProvider>
    </Atlas>
  </>,
);
